import { Button, Collapse, DatePicker, Form, Input, message, Rate, Select } from "antd";
import { NamePath } from "antd/es/form/interface";
import TextArea from "antd/es/input/TextArea";
import { ApiService } from "api/api.service";
import { BlogFeatured, BlogStatus, BlogVisibility } from "components/content-pages/BlogCustomFormItems";
import ImageGalleryModal from "components/content-pages/ImageGalleryModal";
import PageTitle from "components/PageTitle";
import { SingleQueryWrapper } from "components/QueryWrapper";
import RichTextEditor from "components/RichTextEditor";
import dayjs from "dayjs";
import { BlogSitesDto, BlogStatusTypes, BlogVisibilityTypes, ContentType, TwitterCardTypes } from "dtos/blog.dto";
import { T_Category, T_Category_Type } from "dtos/category.dto";
import { CustomerStoryDto, CustomerStorySchemaDto } from "dtos/customerStory.dto";
import DashboardLayout from "layout/DashboardLayout";

import { TbChevronRight } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import { slugify } from "utils/functions";

export default function CustomerStoryItem() {
  const { storySlug } = useParams();

  const customerStory = ApiService.customerStory.useGetByIdOrSlug(storySlug);
  const categoryList = ApiService.category.useGetAllPaginated({
    page: 1,
    limit: 1000,
    type: [T_Category_Type.CUSTOMER_STORY],
  });

  return (
    <SingleQueryWrapper
      query={categoryList}
      renderData={(categoryListData) => {
        if (!storySlug) return <CustomerStoryInternal categories={categoryListData?.data} />;
        return (
          <SingleQueryWrapper
            showLoaderOn="isFetching"
            query={customerStory}
            renderData={(customerStoryData) => {
              return <CustomerStoryInternal customerStory={customerStoryData} categories={categoryListData?.data} />;
            }}
          />
        );
      }}
    />
  );
}

function CustomerStoryInternal({
  customerStory,
  categories,
}: {
  customerStory?: CustomerStorySchemaDto;
  categories: T_Category[];
}) {
  const isEditMode = !!customerStory;
  const [form] = Form.useForm<CustomerStoryDto>();
  const navigate = useNavigate();
  const { data: blogSites } = ApiService.blog.useGetBlogSites();

  function isNotModified(fieldName: NamePath<CustomerStoryDto>) {
    return form.isFieldTouched(fieldName) === false || form.getFieldValue(fieldName)?.length === 0;
  }
  const createCustomerStory = ApiService.customerStory.useCreateBlog();

  const updateCustomerStory = ApiService.customerStory.useUpdateBySlug();

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark="optional"
      preserve={false}
      initialValues={
        isEditMode
          ? {
              ...customerStory,
              published_at: customerStory?.published_at ? dayjs(customerStory?.published_at) : undefined,
              categories: customerStory?.categories?.map((cat) => cat._id),
              blog_sites: customerStory.blog_sites?.map((site) => site._id),
            }
          : {
              status: BlogStatusTypes.DRAFT,
              visiblity: BlogVisibilityTypes.OPEN_TO_ALL,
              featured: false,
              og: {
                ogSiteName: "<site_name>",
              },
              twitter: {
                twitterSite: "<twitter_site_name>",
                twitterAuthor: "<twitter_author>",
                twitterCardType: TwitterCardTypes.SUMMARY_LARGE_IMAGE,
              },
            }
      }
      onFinish={(values) => {
        if (isEditMode) {
          updateCustomerStory.mutate(
            {
              slug: customerStory.slug,
              body: {
                ...values,
                published_at: (values?.published_at as any)?.toISOString(),
              },
            },
            {
              onSuccess: () => {
                message.success("Customer story updated successfully");
                form.resetFields();
                navigate(`/dashboard/customer-stories?page=${1}&limit=${10}`);
              },
            },
          );
        } else {
          createCustomerStory.mutate(
            {
              ...values,
              type: ContentType.CUSTOMER_STORY,
              published_at: (values?.published_at as any)?.toISOString(),
              og: {
                ...values?.og,
                ogUrl: `https://firstchoicedebtsolutions.org/customer-stories/${values?.slug}`,
              },
            },
            {
              onSuccess: () => {
                message.success("Customer story created successfully");
                form.resetFields();
                navigate(`/dashboard/customer-stories?page=${1}&limit=${10}`);
              },
            },
          );
        }
      }}
      onFieldsChange={(changedFields) => {
        if (isEditMode) return;
        const fieldName = changedFields?.[0]?.name?.[0];
        const fieldValue = changedFields?.[0]?.value;

        if (fieldName === "title") {
          if (isNotModified("slug")) {
            form.setFieldValue("slug", `${slugify(fieldValue as string)}`);
          }
          if (isNotModified("pagetitle")) {
            form.setFieldValue("pagetitle", fieldValue);
          }
          if (isNotModified(["og", "seoPageTitle"])) {
            form.setFieldValue(["og", "seoPageTitle"], `${fieldValue} | <site_title>`);
          }
          if (isNotModified(["og", "ogTitle"])) {
            form.setFieldValue(["og", "ogTitle"], `${fieldValue} | <site_title>`);
          }
          if (isNotModified(["twitter", "twitterTitle"])) {
            form.setFieldValue(["twitter", "twitterTitle"], `${fieldValue} | <twitter_title>`);
          }
        } else if (fieldName === "description") {
          if (isNotModified(["og", "seoDescription"])) {
            form.setFieldValue(["og", "seoDescription"], `${fieldValue} | <site_description>`);
          }
          if (isNotModified(["og", "ogDescription"])) {
            form.setFieldValue(["og", "ogDescription"], `${fieldValue} | <site_description>`);
          }
          if (isNotModified(["twitter", "twitterDescription"])) {
            form.setFieldValue(["twitter", "twitterDescription"], `${fieldValue} | <twitter_description>`);
          }
        } else if (fieldName === "feature_image") {
          if (isNotModified(["og", "ogImage"])) {
            form.setFieldValue(["og", "ogImage"], fieldValue);
          }
        }
      }}
    >
      <DashboardLayout
        headerRight={
          <div className="flex gap-3">
            <Form.Item<CustomerStoryDto> name={"status"} className="!m-0">
              <BlogStatus />
            </Form.Item>
            <Form.Item<CustomerStoryDto> name={"visiblity"} className="!m-0">
              <BlogVisibility />
            </Form.Item>
            <Form.Item<CustomerStoryDto> name={"featured"} className="!m-0">
              <BlogFeatured />
            </Form.Item>
            <Button
              className="ml-3"
              color="primary"
              variant="solid"
              onClick={() => form.submit()}
              loading={isEditMode ? updateCustomerStory.isPending : createCustomerStory.isPending}
            >
              {isEditMode ? "Save Changes" : "Create Customer Story"}
            </Button>
          </div>
        }
      >
        <PageTitle
          title={isEditMode ? "Edit Customer Story" : "Add Customer Story"}
          pageTitle={[
            { title: "Customer Story", link: "/dashboard/customer-stories" },
            { title: isEditMode ? "Edit Customer Story" : "Add Customer Story" },
          ]}
        />
        <div className="grid grid-cols-1 lg:grid-cols-[auto_450px]">
          <div className="h-[calc(100vh-50px)] overflow-y-auto">
            <Form.Item<CustomerStoryDto>
              name={"title"}
              rules={[{ required: true, message: "Give customer story a title" }]}
            >
              <Input.TextArea
                className="text-4xl font-extrabold h-fit"
                autoSize={{ minRows: 1, maxRows: 3 }}
                placeholder="Customer Story Title"
                variant="borderless"
              />
            </Form.Item>
            <Form.Item<CustomerStoryDto>
              name={"html"}
              rules={[{ required: true, message: "Write customer story content" }]}
            >
              <RichTextEditor />
            </Form.Item>
          </div>
          <div className="h-[calc(100vh-50px)] overflow-y-auto border-0 border-l border-solid border-gray-200">
            <div className="flex flex-col p-4">
              <Form.Item<CustomerStoryDto> name="feature_image">
                <ImageGalleryModal />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Description" name="description" rules={[{ required: true }]}>
                <TextArea />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Slug" name="slug" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Published At" name="published_at" rules={[{ required: true }]}>
                <DatePicker />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Page Title" name="pagetitle" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Keywords" name="tags" rules={[{ required: true }]}>
                <Select mode="tags" />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Category" name="categories" rules={[{ required: true }]}>
                <Select
                  mode="multiple"
                  allowClear
                  options={categories.map((cat: T_Category) => {
                    return { label: cat.title, value: cat._id };
                  })}
                />
              </Form.Item>
              <Form.Item<CustomerStoryDto> label="Blog Sites" name="blog_sites" rules={[{ required: true }]}>
                <Select
                  mode="multiple"
                  allowClear
                  options={
                    blogSites
                      ? blogSites.map((site: BlogSitesDto) => {
                          return { label: site.name, value: site._id };
                        })
                      : []
                  }
                />
              </Form.Item>
            </div>
            <Collapse
              expandIcon={({ isActive }) => (
                <TbChevronRight
                  className={`transition-transform`}
                  style={{
                    transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                />
              )}
              style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
              defaultActiveKey={0}
              items={[
                {
                  label: "Customer Details",
                  children: (
                    <>
                      <Form.Item<CustomerStoryDto>
                        label="Customer Name"
                        name={["other_data", "cust_name"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto>
                        label="Customer Type"
                        name={["other_data", "cust_type"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto>
                        label="Debt Amount"
                        name={["other_data", "debt_amount"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto>
                        label="Project Name"
                        name={["other_data", "project_name"]}
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto>
                        label="Rating"
                        name={["other_data", "rating"]}
                        rules={[{ required: true }]}
                      >
                        <Rate />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  label: "SEO",
                  children: (
                    <>
                      <Form.Item<CustomerStoryDto> label="SEO Page Title" name={["og", "seoPageTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="SEO Description" name={["og", "seoDescription"]}>
                        <TextArea />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  label: "Open Graph (Facebook, WhatsApp, Instagram)",
                  children: (
                    <>
                      <Form.Item<CustomerStoryDto> label="OG Title" name={["og", "ogTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="OG Description" name={["og", "ogDescription"]}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                      </Form.Item>
                      {/* <Form.Item<CustomerStoryDto> label="OG URL" name={["og", "ogUrl"]}>
                      <Input />
                    </Form.Item> */}
                      <Form.Item<CustomerStoryDto> label="OG Image" name={["og", "ogImage"]}>
                        <ImageGalleryModal />
                        {/* <Input placeholder="Enter image URL or leave blank for thumbnail" /> */}
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="OG Site Name" name={["og", "ogSiteName"]}>
                        <Input defaultValue="<site_name>" />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  label: "Twitter",
                  children: (
                    <>
                      <Form.Item<CustomerStoryDto> label="Card Type" name={["twitter", "twitterCardType"]}>
                        <Select
                          placeholder="Select card type"
                          options={[
                            { label: "Summary", value: TwitterCardTypes.SUMMARY },
                            { label: "Summary Large Image", value: TwitterCardTypes.SUMMARY_LARGE_IMAGE },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="Twitter Title" name={["twitter", "twitterTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="Twitter Description" name={["twitter", "twitterDescription"]}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="Twitter Site" name={["twitter", "twitterSite"]}>
                        <Input placeholder="e.g. @YourSiteHandle" defaultValue={"<twitter_site_name>"} />
                      </Form.Item>
                      <Form.Item<CustomerStoryDto> label="Twitter Author" name={["twitter", "twitterAuthor"]}>
                        <Input placeholder="e.g. @AuthorHandle" defaultValue={"<twitter_author>"} />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </DashboardLayout>
    </Form>
  );
}
